/* MOD-164: the drawer's waits must end — a promise that settles within `ms`, and a fetch aborted after `ms`. */
const moryartyWithin = (promise, ms) =>
  Promise.race([Promise.resolve(promise), new Promise((resolve) => setTimeout(resolve, ms))]);
const moryartySignal = (ms) => {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms); // aborting a finished request does nothing
  return controller.signal;
};
const moryartyFetch = (url, ms, options = {}) => fetch(url, { ...options, signal: moryartySignal(ms) });

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // MOD-164: Check out is blocked (not removed from the focus order) while the drawer is catching up, and
    // while pill taps are still unsent (then the drawer redraws, locked, once they are in)
    this.addEventListener('submit', (event) => {
      if (this.isCheckoutLocked()) {
        event.preventDefault();
      } else if (this.pillsBusy() || this.hasAttribute('data-moryarty-stale')) {
        // MOD-170: also a list not redrawn yet since the last size-table / pill write (see redrawWhenSettled):
        // redraw it now, locked; Check out is pressed again on the fresh list
        event.preventDefault();
        this.markStale();
        this.refreshIfStale();
      } else {
        this.syncLineQuantities(); // MOD-168
      }
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  /* MOD-152 START — cart view (Normal / Compact / Grid) and the B2B lazy cart list.
     The view lives on <cart-drawer> itself, which no re-render ever replaces (they only swap its insides).
     The server default is Compact for B2B buyers (data-cart-view="compact"; Grid until MOD-178), Normal otherwise; the shopper's own
     choice is remembered per browser, separately for B2B and retail sessions. Applying it here is
     flicker-free: the drawer can only be shown by open(), which needs this script. */
  connectedCallback() {
    let view = this.dataset.cartView || 'normal';
    try {
      const stored = localStorage.getItem(this.cartViewKey());
      if (MORYARTY_CART_VIEWS.includes(stored)) view = stored;
    } catch (e) {}
    this.dataset.cartView = view;
    // B2B lines carry a srcset (Grid default): keep each thumbnail's `sizes` in step with the view so
    // Compact/Normal don't download Grid-sized files. Set on insertion, before the lazy image is fetched.
    if (this.hasAttribute('data-b2b')) {
      this.syncImageSizes();
      new MutationObserver(() => this.syncImageSizes()).observe(this, { childList: true, subtree: true });
    }
  }

  syncImageSizes() {
    const sizes = MORYARTY_CART_IMAGE_SIZES[this.dataset.cartView] || MORYARTY_CART_IMAGE_SIZES.normal;
    this.querySelectorAll('.cart-item__image[srcset]').forEach((image) => {
      if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
    });
  }

  cartViewKey() {
    return this.hasAttribute('data-b2b') ? 'moryarty-cart-view-b2b' : 'moryarty-cart-view';
  }

  setCartView(view) {
    if (!MORYARTY_CART_VIEWS.includes(view)) return;
    this.dataset.cartView = view;
    try {
      localStorage.setItem(this.cartViewKey(), view);
    } catch (e) {}
    this.querySelectorAll('cart-view-switch').forEach((viewSwitch) => viewSwitch.sync());
    if (this.hasAttribute('data-b2b')) this.syncImageSizes();
  }

  /* B2B page renders leave the cart list out (data-moryarty-lazy-cart). Fetch it on intent (hover, touch
     or focus of the cart icon) and build it when the drawer opens. Any later cart change re-renders the
     drawer from a full section render, which removes the placeholder for good. */
  prefetchLazyCart() {
    if (!this.querySelector('[data-moryarty-lazy-cart]')) return Promise.resolve();
    // A prefetch older than 30 s may predate a change made in another tab: fetch again.
    if (this.lazyCartPromise && Date.now() - this.lazyCartFetchedAt < 30000) return this.lazyCartPromise;
    this.lazyCartFetchedAt = Date.now();
    const generation = this.cartGeneration || 0; // MOD-164: a cart change after this fetch started voids its result
    this.lazyCartPromise = moryartyFetch(`${routes.cart_url}?section_id=cart-drawer`, 10000) // MOD-164: time limit
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        if (generation !== (this.cartGeneration || 0)) return; // MOD-164: older than a later cart change
        this.lazyCartHtml = html;
        this.lazyCartGeneration = generation;
      })
      .catch(() => {
        this.lazyCartPromise = null;
      });
    return this.lazyCartPromise;
  }

  /* Returns true when the list is in place (built now, or already replaced by a full render). Only the list
     and the totals are swapped: the header and footer stay, so a button the buyer already focused stays. */
  buildLazyCart() {
    const html = this.lazyCartHtml;
    this.lazyCartHtml = null;
    this.lazyCartPromise = null;
    if (html) this.builtGeneration = this.lazyCartGeneration; // MOD-164: which cart state the list shows
    if (!this.querySelector('[data-moryarty-lazy-cart]')) return true;
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const container = document.getElementById('CartDrawer');
    if (doc.querySelector('cart-drawer.is-empty')) {
      // Emptied meanwhile (another tab): take the whole drawer, empty state included.
      const freshInner = doc.querySelector('#CartDrawer');
      if (!freshInner) return false;
      container.innerHTML = freshInner.innerHTML;
      this.classList.add('is-empty');
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        trapFocus(this.querySelector('.drawer__inner-empty'), this.querySelector('.drawer__inner-empty a'));
      }
      return true;
    }
    const freshItems = doc.querySelector('cart-drawer-items');
    const items = this.querySelector('cart-drawer-items');
    if (!freshItems || !items) return false;
    items.replaceWith(freshItems);
    const freshTotals = doc.querySelector('.cart-drawer__footer');
    const totals = this.querySelector('.cart-drawer__footer');
    if (freshTotals && totals) totals.replaceWith(freshTotals);
    // Dawn's focus trap remembers the first/last focusable elements, which may just have been replaced.
    if (this.classList.contains('active')) {
      const keep = container.contains(document.activeElement) ? document.activeElement : this.querySelector('.drawer__inner');
      trapFocus(container, keep);
    }
    return true;
  }

  showLazyCartError() {
    const placeholder = this.querySelector('[data-moryarty-lazy-cart]');
    if (!placeholder) return;
    placeholder.querySelector('.cart-drawer__lazy-loading')?.setAttribute('hidden', '');
    placeholder.querySelector('.cart-drawer__lazy-error')?.removeAttribute('hidden');
  }
  /* MOD-152 END */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    // MOD-152: B2B lazy cart list — start fetching it as soon as the shopper heads for the cart icon
    if (this.querySelector('[data-moryarty-lazy-cart]')) {
      const prefetch = () => this.prefetchLazyCart();
      ['pointerenter', 'touchstart', 'focus'].forEach((type) =>
        cartLink.addEventListener(type, prefetch, { passive: true })
      );
    }
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* MOD-161 START — cart changes made outside the drawer that don't redraw it (the B2B order pills) mark it
     stale instead of re-rendering a long cart on every tap; it is redrawn from the server on its next open
     (list faded meanwhile). A prefetched lazy list (MOD-152) is dropped too. */
  markStale() {
    this.cartGeneration = (this.cartGeneration || 0) + 1; // MOD-164: voids lazy-list fetches already started
    this.lazyCartHtml = null;
    this.lazyCartPromise = null;
    this.setAttribute('data-moryarty-stale', '');
    // MOD-163: already open → redraw. MOD-170: not at once — the buyer may be tapping or typing in the size table
    // (each of those writes marks the drawer stale): once the pills queue is idle and no size box has focus.
    if (this.classList.contains('active')) this.redrawWhenSettled();
    this.updateBusy(); // MOD-164: a list swapped in meanwhile (an older refetch) takes the current lock
  }

  // MOD-170: a redraw locks the list (inert) and replaces it — never while a size-table write is waiting or the
  // buyer is in a size box (a half-typed quantity would be committed, later keystrokes lost). Check out redraws
  // at once (submit guard).
  redrawWhenSettled() {
    window.clearTimeout(this.settleTimer);
    const check = () => {
      if (!this.hasAttribute('data-moryarty-stale') || !this.classList.contains('active')) return;
      const typing = document.activeElement && document.activeElement.matches && document.activeElement.matches('cart-drawer .cart-size .mop__qty');
      if (this.pillsBusy() || typing) {
        this.settleTimer = window.setTimeout(check, 400);
        return;
      }
      this.refreshIfStale();
    };
    this.settleTimer = window.setTimeout(check, 400);
  }

  // MOD-170: a refused size-table write — said in the drawer's own alert box (inside the dialog, so read out),
  // and put back after a redraw for a few seconds (the box is part of the list a redraw replaces).
  showNote(text) {
    this.note = { text, until: Date.now() + 6000 };
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = text;
  }

  keepNote() {
    if (!this.note || Date.now() > this.note.until) return;
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors && !errors.textContent) errors.textContent = this.note.text;
  }

  /* Returns a promise when a redraw starts (it resolves once the list is fresh), else null. MOD-163: first
     sends any pill taps still waiting (window.moryartyB2BPills.idle) so the redraw includes them, and keeps
     the drawer locked meanwhile (MOD-164: list AND Check out, see updateBusy). */
  refreshIfStale() {
    if (this.refreshing) return this.refreshing;
    if (!this.hasAttribute('data-moryarty-stale')) return null;
    if (this.querySelector('[data-moryarty-lazy-cart]')) {
      this.removeAttribute('data-moryarty-stale'); // the lazy list fetches a fresh copy anyway (after the pills)
      return null;
    }
    // MOD-164: every step has a time limit; a failed redraw keeps the old list locked (showRefreshError)
    let redrawn = false;
    this.refreshing = moryartyWithin(this.pillsIdle(), 10000)
      .then(() => {
        this.removeAttribute('data-moryarty-stale');
        return moryartyFetch(`${routes.cart_url}?section_id=cart-drawer`, 10000);
      })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return;
        this.refreshSections({ 'cart-drawer': html });
        redrawn = true;
      })
      .catch(() => {})
      .finally(() => {
        this.refreshing = null;
        if (!redrawn) this.showRefreshError();
        // MOD-164: taps made while it waited (drawer closed, pill tapped, reopened) → one more locked redraw
        else if (this.pillsBusy()) this.setAttribute('data-moryarty-stale', '');
        this.updateBusy();
        if (!redrawn) return; // the next open tries again (no retry loop when offline)
        if (this.classList.contains('active') && this.hasAttribute('data-moryarty-stale')) this.refreshIfStale();
        else if (this.shouldMergeDrawer()) this.mergeAndRefresh();
      });
    this.updateBusy();
    return this.refreshing;
  }
  /* MOD-161 END */

  /* MOD-164 START — one lock for every wait: a redraw after pill taps (refreshIfStale), a duplicate merge
     (mergeAndRefresh, MOD-159) and the B2B lazy list's first load (open). Check out posts the form's
     updates[], which Shopify applies by line POSITION, so from an old list they land on the wrong lines (and
     taps still waiting would be left behind):
     - while a redraw or a merge runs, the list is faded and inert;
     - while any wait runs, Check out is blocked (submit guard in the constructor, aria-disabled, footer
       faded). It stays focusable so Dawn's focus trap keeps its last element;
     - a list that could not be redrawn (refreshFailed) stays locked, and its quantity boxes are disabled so
       Check out sends no old quantities; the next open tries again.
     Each wait calls this when it starts and ends, and every redraw calls it again (a redraw brings unlocked
     elements). */
  pillsBusy() {
    return !!(window.moryartyB2BPills && window.moryartyB2BPills.busy());
  }

  pillsIdle() {
    return window.moryartyB2BPills ? window.moryartyB2BPills.idle() : null;
  }

  isCheckoutLocked() {
    // MOD-168: also while a B2B "remove this poster" is on its way, and (B2B) while a drawer −/+ change is: Check
    // out posts updates[] by position, which a line removed meanwhile would shift onto the next lines
    return !!(
      this.refreshing ||
      this.merging ||
      this.lazyWaits > 0 ||
      this.removingLines ||
      (this.hasAttribute('data-b2b') &&
        document.getElementById('CartDrawer-CartItems')?.classList.contains('cart__items--disabled'))
    );
  }

  updateBusy() {
    const listBusy = !!(this.refreshing || this.merging || this.refreshFailed || this.removingLines); // MOD-168
    const list = document.getElementById('CartDrawer-CartItems');
    if (list) {
      // focus can't stay on an inert control: keep it in the dialog
      if (listBusy && list.contains(document.activeElement)) {
        this.querySelector('.drawer__inner')?.focus({ preventScroll: true });
      }
      list.inert = listBusy;
      list.classList.toggle('cart-drawer__merging', listBusy); // faded (component-cart-drawer.css)
      if (this.refreshFailed) list.querySelectorAll('input[name="updates[]"]').forEach((input) => (input.disabled = true));
    }
    // an empty-at-load drawer redrawn after pill taps: its "Your cart is empty" is old too
    this.querySelector('.drawer__inner-empty')?.classList.toggle('cart-drawer__merging', !!this.refreshing);
    const checkoutLocked = this.isCheckoutLocked();
    this.querySelector('.drawer__footer')?.classList.toggle('cart-drawer__merging', checkoutLocked);
    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout) {
      if (checkoutLocked) checkout.setAttribute('aria-disabled', 'true');
      else checkout.removeAttribute('aria-disabled');
    }
  }

  // A full, fresh list arrived (any redraw from the server): it can be used again.
  listRefreshed() {
    this.refreshFailed = false;
    this.updateBusy();
  }

  showRefreshError() {
    this.refreshFailed = true;
    this.cartGeneration = (this.cartGeneration || 0) + 1; // a list fetched before this can't clear it
    this.setAttribute('data-moryarty-stale', '');
    const text = window.cartStrings ? window.cartStrings.error : '';
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = text;
    // the list (and its error box) is hidden in the empty state: say it there instead
    const empty = this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty');
    if (empty && text) {
      let message = empty.querySelector('.cart-drawer__refresh-error');
      if (!message) {
        message = document.createElement('p');
        message.className = 'cart-drawer__refresh-error';
        message.setAttribute('role', 'alert');
        empty.appendChild(message);
      }
      message.textContent = text;
    }
  }
  /* MOD-164 END */

  /* MOD-168 START — B2B grouped list: its quantity boxes have no name (the rows are grouped by product, not in
     cart order, and Check out posts updates[] by line POSITION). Right before the form is posted, each box's
     value goes into its hidden updates[] field, which the Liquid lists in cart order (cart-drawer.liquid) — so
     Check out sends exactly what the retail drawer sends: the visible quantities, each on its own line. Fields
     disabled after a failed redraw (MOD-164) are left alone and not posted. */
  syncLineQuantities() {
    this.querySelectorAll('input[data-moryarty-line]').forEach((field) => {
      if (field.disabled) return;
      const box = this.querySelector(`.quantity__input[data-quantity-line-key="${CSS.escape(field.dataset.moryartyLine)}"]`);
      if (!box || box.disabled) return;
      const value = parseInt(box.value, 10);
      if (Number.isInteger(value) && value >= 0) field.value = value;
    });
  }
  /* MOD-168 END */

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // MOD-163: pill taps still waiting or being sent → treat the drawer as stale now, so it waits for them
    // (locked) instead of showing the old cart until the first one lands.
    if (window.moryartyB2BPills && window.moryartyB2BPills.busy()) this.setAttribute('data-moryarty-stale', '');
    const refreshing = this.refreshIfStale(); // MOD-161/163
    // MOD-159: duplicates that came without an add (Globo's Edit options on /cart, carts from before MOD-157)
    if (!refreshing && this.shouldMergeDrawer()) this.mergeAndRefresh();
    // MOD-152: B2B lazy cart list — open straight away (the placeholder shows a spinner) and fill it in.
    // MOD-163: after any pill taps still waiting have been sent, so the list includes them.
    // MOD-164: Check out locked until then; the view event is sent from the built list (see below).
    let lazyBuilt = null;
    if (this.querySelector('[data-moryarty-lazy-cart]')) {
      this.lazyWaits = (this.lazyWaits || 0) + 1; // a count: a second open while the first still waits
      this.updateBusy();
      // up to 3 tries: a cart change during a fetch voids it (generation), so wait for the taps and fetch again
      const buildAfterTaps = async () => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          await moryartyWithin(this.pillsIdle(), 10000);
          await this.prefetchLazyCart();
          if (this.buildLazyCart()) return true;
        }
        return false;
      };
      lazyBuilt = buildAfterTaps()
        .catch(() => false)
        .then((built) => {
          if (!built) this.showLazyCartError();
          return built;
        })
        .finally(() => {
          this.lazyWaits -= 1;
          // taps made while it waited, or a cart change after its fetch, are not in the built list: redraw it
          // (locked) once they are sent
          const built = !this.querySelector('[data-moryarty-lazy-cart]');
          if (this.pillsBusy() || (built && this.builtGeneration !== (this.cartGeneration || 0))) this.markStale();
          this.updateBusy();
        });
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    // MOD-163/164: after a redraw or the lazy list's build, from the fresh list (the old one, and the
    // placeholder, carry the cart from page load); a redraw that triggered another is waited for too.
    // MOD-164: not sent from a list that failed to load or redraw (it would report an old cart).
    const dispatchViewEvent = () => {
      if (this.refreshFailed || this.querySelector('[data-moryarty-lazy-cart]')) return;
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    };
    const wait = refreshing || lazyBuilt;
    if (wait) wait.then(() => this.refreshing).then(dispatchViewEvent);
    else dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    /* MOD-157/159 START — merge identical Globo lines. Globo stamps every add with its own _gpo_product_group
       (a timestamp), so the same framed poster added twice became two lines ×1 in Shopify's cart — drawer,
       /cart, checkout and order. Every add (product-form and Globo) ends here. MOD-159: the add's own result
       is shown and the drawer opens straight away, as before MOD-157 (waiting for the merge left a gap in
       which a second tap added one more poster); when that result shows two identical Globo rows, they are
       folded in place right after (see mergeAndRefresh). */
    const hasDuplicates = this.hasDuplicateGloboRows(parsedState.sections && parsedState.sections['cart-drawer']);
    /* MOD-157/159 END */
    /* MOD-061 START: also clear `is-empty` on the <cart-drawer> element itself (not just on
       .drawer__inner). Dawn normally strips it in product-form.js's add handler, but Globo Product
       Options bypasses that handler — so adding to an EMPTY cart left <cart-drawer> with `is-empty`,
       which (a) re-grids the inner to the empty-state layout (items hidden, header gone, text
       mispositioned) and (b) crashed open()'s trapFocus (null `.drawer__inner-empty`). The cart was
       always correct server-side; only this stale class broke the live drawer. */
    this.classList.remove('is-empty');
    /* MOD-061 END */
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.removeAttribute('data-moryarty-stale'); // MOD-161: a full render is fresh
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.listRefreshed(); // MOD-164: fresh list, but a wait still running keeps it locked

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
    if (hasDuplicates) this.mergeAndRefresh(); // MOD-159
  }

  /* MOD-157/159 START — see renderContents. A cheap check on drawer markup (the HTML an add returned, or the
     drawer itself): two Globo-configured rows (they carry the edit-options link) with the same variant and
     the same options. */
  hasDuplicateGloboRows(source) {
    if (!source) return false;
    const root = typeof source === 'string' ? new DOMParser().parseFromString(source, 'text/html') : source;
    const seen = new Set();
    return [...root.querySelectorAll('.cart-item')].some((row) => {
      // Globo rows only; never a poster with separate add-on lines (Merge OFF, MOD-152's cart-item--no-qty)
      // MOD-168: the B2B grouped list marks such rows with data-moryarty-gpo (it has no edit-options link)
      if (!(row.hasAttribute('data-moryarty-gpo') || row.querySelector('.cart-item__gpo-edit')) || row.classList.contains('cart-item--no-qty')) return false;
      const variantId = row.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId || '';
      const options = row.querySelector('.cart-item__details dl')?.textContent.replace(/\s+/g, ' ').trim() || '';
      const signature = `${variantId}|${options}`;
      if (seen.has(signature)) return true;
      seen.add(signature);
      return false;
    });
  }

  /* MOD-159: fold the duplicates and redraw the drawer in place (it never re-opens a drawer the shopper
     closed). The list is dimmed and blocked meanwhile (Dawn's own busy class). When there was nothing left to
     merge — an earlier merge already did it — the drawer is redrawn from the server anyway, so a render that
     predates that merge can't stay on screen. */
  // The drawer's line keys, to remember a set of rows that looked like duplicates but couldn't be merged.
  drawerLineKeys() {
    return [...this.querySelectorAll('[data-quantity-line-key]')].map((input) => input.dataset.quantityLineKey).sort().join(',');
  }

  // For the triggers without an add (open, a drawer refetch): duplicates on screen that weren't already
  // found unmergeable, no merge running.
  shouldMergeDrawer() {
    return (
      !this.mergeInFlight &&
      !this.querySelector('[data-moryarty-lazy-cart]') &&
      this.hasDuplicateGloboRows(this) &&
      this.drawerLineKeys() !== this.unmergeableKeys
    );
  }

  mergeAndRefresh() {
    // Not on the /cart page: Globo pairs that page's rows with the cart by position, so changing the cart
    // under it would make the page's next change hit the wrong line. The merge happens on the next page.
    if (document.getElementById('main-cart-items')) return Promise.resolve();
    // One merge at a time; an add that arrives meanwhile asks for one more pass (which re-reads the cart).
    if (this.mergeInFlight) {
      this.mergeAgain = true;
      return this.mergeInFlight;
    }
    const pass = () => {
      this.updateBusy(); // MOD-164: list + footer locked (shared with the pill redraw)
      return this.mergeDuplicateGloboLines()
        .then(async (merged) => {
          if (merged) {
            this.unmergeableKeys = null;
            return merged.sections;
          }
          const response = await moryartyFetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, 10000);
          return response.ok ? response.json() : null;
        })
        .then((sections) => {
          // MOD-164: no fresh drawer (failed, or Shopify rendered no section): the list may predate the merge
          if (sections && sections['cart-drawer']) this.refreshSections(sections);
          else this.showRefreshError();
          // Still showing duplicates after a pass that merged nothing: leave this set of rows alone.
          if (this.hasDuplicateGloboRows(this)) this.unmergeableKeys = this.drawerLineKeys();
        });
    };
    const loop = async () => {
      do {
        this.mergeAgain = false;
        await pass();
      } while (this.mergeAgain);
    };
    this.merging = true; // MOD-164: set before the first pass, which locks the drawer synchronously
    this.mergeInFlight = loop()
      .catch(() => this.showRefreshError()) // MOD-164
      .finally(() => {
        this.mergeInFlight = null;
        this.merging = false;
        this.updateBusy();
      });
    return this.mergeInFlight;
  }

  refreshSections(sections) {
    const container = document.getElementById('CartDrawer');
    const drawerHtml = sections['cart-drawer'];
    // keep the shopper's place: scroll positions, and focus when the focused control has an id
    const scrolls = ['cart-drawer-items', '.drawer__inner'].map((selector) => [selector, this.querySelector(selector)?.scrollTop || 0]);
    const focusedId = container && container.contains(document.activeElement) ? document.activeElement.id : '';
    if (container && drawerHtml) {
      const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const freshInner = doc.querySelector('#CartDrawer');
      if (freshInner) {
        container.innerHTML = freshInner.innerHTML;
        this.classList.toggle('is-empty', !!doc.querySelector('cart-drawer.is-empty'));
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      }
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      bubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
    }
    scrolls.forEach(([selector, top]) => {
      const element = this.querySelector(selector);
      if (element) element.scrollTop = top;
    });
    if (this.classList.contains('active') && container) {
      const emptyBlock = this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty');
      const keep = (focusedId && document.getElementById(focusedId)) || this.querySelector('.drawer__inner');
      trapFocus(emptyBlock || container, keep);
      if (keep && keep !== document.activeElement) keep.focus({ preventScroll: true });
    }
    // MOD-164: the fresh elements take the current lock (another wait may still run)
    if (container && drawerHtml) this.listRefreshed();
    else this.updateBusy();
    this.keepNote(); // MOD-170
  }

  /* The cart itself decides: lines are identical when variant, selling plan and every property match except
     _gpo_product_group. The oldest line (new lines are inserted at the top) keeps the summed quantity, the
     others go to 0 — one /cart/update request, keyed by line key. Resolves to that response (cart + the
     drawer sections) or null; one merge at a time. TAGGRS empties its cart snapshot on every add, so this
     follow-up change right after an add is not reported as an extra add/remove. */
  mergeDuplicateGloboLines() {
    const run = async () => {
      const cart = await moryartyFetch(`${routes.cart_url}.js`, 10000).then((response) => response.json()); // MOD-164
      // MOD-159: never touch a poster whose Globo add-ons are separate lines (Merge OFF) — changing its
      // quantity alone would strand them (same rule as MOD-152's cart-item--no-qty).
      const parentGroups = new Set(cart.items.map((item) => item.properties?._gpo_parent_product_group).filter(Boolean));
      const groups = new Map();
      cart.items.forEach((item) => {
        const properties = item.properties || {};
        if (!properties._gpo_product_group) return;
        const rest = Object.keys(properties)
          .filter((key) => key !== '_gpo_product_group')
          .sort()
          .map((key) => [key, properties[key]]);
        const signature = JSON.stringify([item.variant_id, item.selling_plan_allocation?.selling_plan?.id || null, rest]);
        if (!groups.has(signature)) groups.set(signature, []);
        groups.get(signature).push(item);
      });
      const updates = {};
      groups.forEach((lines) => {
        if (lines.length < 2) return;
        if (lines.some((line) => parentGroups.has(line.properties._gpo_product_group))) return;
        const oldest = lines[lines.length - 1];
        updates[oldest.key] = lines.reduce((sum, line) => sum + line.quantity, 0);
        lines.slice(0, -1).forEach((line) => {
          updates[line.key] = 0;
        });
      });
      if (!Object.keys(updates).length) return null;
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        signal: moryartySignal(10000), // MOD-164: a stalled merge must not hold Check out
        body: JSON.stringify({
          updates,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      return response.ok && state.sections ? state : null;
    };
    this.globoMerge = (this.globoMerge || Promise.resolve()).then(run, run).catch(() => null);
    return this.globoMerge;
  }
  /* MOD-157 END */

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

// MOD-152: the three cart views (see CartDrawer.connectedCallback), and each view's thumbnail width
const MORYARTY_CART_VIEWS = ['normal', 'compact', 'grid'];
const MORYARTY_CART_IMAGE_SIZES = {
  normal: '(min-width: 750px) 100px, 80px',
  compact: '56px',
  grid: '(min-width: 750px) 190px, 50vw',
};

customElements.define('cart-drawer', CartDrawer);

/* MOD-152 START — the Normal / Compact / Grid buttons in the drawer header. The header is re-rendered on
   every quantity change, so each new switch re-reads the drawer's view when it connects. */
class CartViewSwitch extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-view]');
      if (button) this.closest('cart-drawer')?.setCartView(button.dataset.view);
    });
  }

  connectedCallback() {
    this.sync();
  }

  sync() {
    const view = this.closest('cart-drawer')?.dataset.cartView || 'normal';
    this.querySelectorAll('[data-view]').forEach((button) =>
      button.setAttribute('aria-pressed', button.dataset.view === view ? 'true' : 'false')
    );
  }
}

customElements.define('cart-view-switch', CartViewSwitch);
/* MOD-152 END */

class CartDrawerItems extends CartItems {
  /* MOD-152 START — keep the shopper's place across a quantity change. The update replaces the whole list
     (scroll back to 0) and Dawn then focuses the clicked button by name — but Safari and Firefox on macOS
     don't focus a clicked button, so Dawn fell back to the FIRST line and every tap jumped the list to the
     top. Remember the tapped button and the scroll positions, restore them after the re-render. */
  constructor() {
    super();
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.quantity__button');
        if (button) CartDrawerItems.lastQuantityButton = button.name;
      },
      true
    );
    // Enter in a quantity box: the drawer form's only submit button is Check out, so the browser would
    // "submit" the form there (stock Dawn; the stepper is visible again). Cancel that and blur, as Dawn's
    // quick-order list does: the browser then sends exactly one change, and none if nothing was edited.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing || !event.target.matches('.quantity__input')) return;
      event.preventDefault();
      event.target.blur();
    });
  }

  moryartyKeepPlace(lineKey, name) {
    // If the line goes (removed), focus moves to its neighbour's product name — never onto a neighbour's
    // −/remove button, where the next key press would act on another product.
    const rowOf = (key) =>
      key && document.querySelector(`cart-drawer [data-quantity-line-key="${CSS.escape(key)}"]`)?.closest('.cart-item');
    const row = rowOf(lineKey);
    // MOD-168: in the B2B grouped list the rows sit in one <tbody> per product, next to its image and title rows:
    // take the neighbouring LINE in the whole list, and focus its product's title (size rows have none)
    const grouped = !!row?.closest('.cart-group');
    const neighbour = grouped ? this.moryartyNeighbourLine(row) : row && (row.nextElementSibling || row.previousElementSibling);
    const neighbourKey = neighbour?.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;
    const scrollers = ['cart-drawer-items', '.drawer__inner'].map((selector) => {
      const element = document.querySelector(`cart-drawer ${selector}`);
      return [selector, element ? element.scrollTop : 0];
    });
    const buttonName = name || CartDrawerItems.lastQuantityButton;
    CartDrawerItems.lastQuantityButton = null;
    return () => {
      scrollers.forEach(([selector, top]) => {
        const element = document.querySelector(`cart-drawer ${selector}`);
        if (element) element.scrollTop = top;
      });
      const sameRow = rowOf(lineKey);
      if (sameRow) {
        const input = sameRow.querySelector('.quantity__input');
        const target = (buttonName && sameRow.querySelector(`[name="${buttonName}"]:not([disabled])`)) || input;
        target?.focus({ preventScroll: true });
        return;
      }
      const neighbourRow = rowOf(neighbourKey);
      const name = grouped
        ? neighbourRow?.closest('.cart-group')?.querySelector('.cart-item__name')
        : neighbourRow?.querySelector('.cart-item__name');
      name?.focus({ preventScroll: true });
      if (grouped) name?.scrollIntoView({ block: 'nearest' }); // MOD-168: only if it ended up out of view
    };
  }
  /* MOD-152 END */

  /* MOD-168 START — B2B grouped list (snippets/moryarty-cart-drawer-groups.liquid). */
  // The line row after this one in the whole list (else the one before), across product groups.
  moryartyNeighbourLine(row) {
    const rows = [...this.querySelectorAll('.cart-item')];
    const at = rows.indexOf(row);
    if (at < 0) return null;
    return rows[at + 1] || rows[at - 1] || null;
  }

  /* Grid view: "remove this poster" — every size of one product to 0 in ONE /cart/update request, by line key
     (cart writes sent together lose updates). The drawer is then redrawn from that answer, exactly like a −/+
     change (cart.js updateQuantity), and the change is announced to the pills and the Globo cleanup. Check out
     is held meanwhile (CartDrawer.isCheckoutLocked). */
  async moryartyRemoveLines(keys, productId) {
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (!keys.length || !drawer || drawer.removingLines) return;
    drawer.removingLines = true;
    drawer.updateBusy(); // from the tap on: list faded + inert (keyboard too, focus kept in the dialog), Check out held
    // A −/+ change still waiting (debounce) or on its way goes first: cart writes sent together lose updates. Its
    // redraw may replace this element, so everything below is looked up in the drawer.
    await this.moryartyDrawerIdle();
    // MOD-170: the product's lines as the cart has them now — a size added from the size table since the list
    // was drawn is removed too (the keys in the markup predate it)
    if (productId) {
      try {
        const now = await moryartyFetch(`${routes.cart_url}.js`, 10000).then((response) => (response.ok ? response.json() : null));
        if (now && Array.isArray(now.items)) {
          const fresh = now.items.filter((item) => String(item.product_id) === String(productId)).map((item) => item.key);
          if (fresh.length) keys = fresh;
        }
      } catch (error) {
        // keep the keys from the markup
      }
    }
    drawer.cartGeneration = (drawer.cartGeneration || 0) + 1; // MOD-164: a refetch started before this is void
    const list = document.getElementById('CartDrawer-CartItems');
    list?.classList.add('cart__items--disabled');
    const rowOf = (key) => drawer.querySelector(`[data-quantity-line-key="${CSS.escape(key)}"]`)?.closest('.cart-item');
    keys.forEach((key) => rowOf(key)?.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.remove('hidden')));
    // focus afterwards: the next product's title (else the previous one's)
    const groups = [...drawer.querySelectorAll('.cart-group')];
    const at = groups.indexOf(keys.map(rowOf).find(Boolean)?.closest('.cart-group'));
    const nextGroup = at >= 0 ? groups[at + 1] || groups[at - 1] : null;
    const nextKey = nextGroup?.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;
    const scrollers = ['cart-drawer-items', '.drawer__inner'].map((selector) => {
      const element = drawer.querySelector(selector);
      return [selector, element ? element.scrollTop : 0];
    });
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    let deferred = null;
    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      (drawer.querySelector('cart-drawer-items') || this).dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: keys.map((id) => ({ id, quantity: 0 })),
          promise: deferred.promise,
        })
      );
    }
    const sectionsToRender = this.getSectionsToRender();
    try {
      // TAGGRS forgets its copy of the cart after every add; refill it so these removals are reported (as the
      // B2B pills do before a change).
      if (window.taggrsCart && typeof window.taggrsCart.ensureSeed === 'function') {
        await Promise.race([
          Promise.resolve(window.taggrsCart.ensureSeed()).catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      }
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        signal: typeof moryartySignal === 'function' ? moryartySignal(10000) : undefined,
        body: JSON.stringify({
          updates: Object.fromEntries(keys.map((key) => [key, 0])),
          sections: sectionsToRender.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || !state.sections || !Array.isArray(state.items)) throw new Error(response.status);
      drawer.classList.toggle('is-empty', state.item_count === 0);
      sectionsToRender.forEach((section) => {
        const target =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        target.innerHTML = this.getSectionInnerHTML(state.sections[section.section], section.selector);
      });
      drawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', state.item_count === 0);
      drawer.removingLines = false;
      drawer.listRefreshed(); // MOD-164: unlocked, unless another wait still runs
      scrollers.forEach(([selector, top]) => {
        const element = drawer.querySelector(selector);
        if (element) element.scrollTop = top;
      });
      const empty = state.item_count === 0 && drawer.querySelector('.drawer__inner-empty');
      if (empty) {
        trapFocus(empty, empty.querySelector('a'));
      } else {
        const keep =
          (nextKey && rowOf(nextKey)?.closest('.cart-group')?.querySelector('.cart-item__name')) ||
          drawer.querySelector('.drawer__inner');
        trapFocus(document.getElementById('CartDrawer'), keep);
      }
      if (deferred) deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(state) });
      publish(PUB_SUB_EVENTS.cartUpdate, { source: this.localName, cartData: state });
    } catch (error) {
      deferred?.reject(error);
      // The lines may or may not be gone (an answer lost after the change was applied): MOD-164's failed-redraw
      // path — the old list's quantity fields disabled, then a locked redraw from the server (it retries on the
      // next open if that fails too). `refreshing` holds Check out from here, so the lock has no gap.
      drawer.showRefreshError();
      drawer.refreshIfStale();
    } finally {
      drawer.removingLines = false;
      document.getElementById('CartDrawer-CartItems')?.classList.remove('cart__items--disabled');
      drawer.updateBusy();
    }
  }

  // Resolves once no drawer −/+ change is waiting or being sent (at most ~5 s).
  async moryartyDrawerIdle() {
    // MOD-170: first the size table's (and the card pills') writes — one queue, see moryarty-b2b-order-pills.js —
    // then the redraw they started: a list fetched before this removal must not land after it
    const drawer = document.querySelector('cart-drawer');
    if (window.moryartyB2BPills && window.moryartyB2BPills.busy()) await moryartyWithin(window.moryartyB2BPills.idle(), 10000);
    if (drawer && drawer.refreshing) await moryartyWithin(drawer.refreshing, 10000);
    for (let attempt = 0; attempt < 50; attempt += 1) {
      const list = document.getElementById('CartDrawer-CartItems');
      const sending = list?.classList.contains('cart__items--disabled');
      const waiting = [...document.querySelectorAll('cart-drawer .quantity__input')].some(
        (box) => box.value !== box.getAttribute('value')
      );
      if (!sending && !waiting) return;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }
  /* MOD-168 END */

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* MOD-168 START — the Grid view's "remove this poster" button in the B2B grouped list (data-keys = the
   product's line keys, comma-separated). */
class CartGroupRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const keys = (this.dataset.keys || '').split(',').filter(Boolean);
      this.closest('cart-drawer-items')?.moryartyRemoveLines(keys, this.dataset.product); // MOD-170: product id
    });
  }
}

customElements.define('cart-group-remove-button', CartGroupRemoveButton);
/* MOD-168 END */
